import { useEffect, useState } from "react";
import { ContainerStatus } from "./SasTokenDisplay";
import { Button, useModals, Text } from "../ui";
import { getContainerActions, useContainer } from "../hooks/useContainer";
import { CorsHelp } from "./CorsHelp";

// Constants for configuration
const PLACEHOLDER_TEXT = "SAS Link";
const MODAL_TITLE = "CORS Check";
const MODAL_SIZE = "lg" as const;
const URL_PARAM_NAME = "containerUrl";

// Hook for managing URL parameters
const useUrlParams = () => {
  // Get URL parameter value
  const getUrlParam = (name: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  };

  // Set URL parameter
  const setUrlParam = (name: string, value: string) => {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(name, value);
    } else {
      url.searchParams.delete(name);
    }
    window.history.replaceState({}, '', url.toString());
  };

  return { getUrlParam, setUrlParam };
};

// Container URL input component
const UrlInput = ({ value, onChange, onSave }: {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
}) => (
  <div className="flex">
    <input
      placeholder={PLACEHOLDER_TEXT}
      className="border w-full rounded-md p-2 bg-gray-50 mr-4"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    <Button onClick={onSave}>save</Button>
  </div>
);

// CORS error display component
const CorsError = ({ corsStatus }: { corsStatus: any }) => {
  if (!corsStatus || corsStatus.success) return null;
  
  return (
    <div className="mt-2 text-sm text-red-700">
      {corsStatus.error}
    </div>
  );
};

// Hook for CORS help modal
const useCorsHelp = () => {
  const { open } = useModals();
  
  return () => open({
    title: MODAL_TITLE,
    size: MODAL_SIZE,
    content: <CorsHelp />,
  });
};

// Container management component for storage URL configuration
export const ItemContainer = ({ url }: { url?: string }) => {
  const { getUrlParam, setUrlParam } = useUrlParams();
  const container = useContainer();
  const [urlValue, setUrlValue] = useState<string>(() => {
    // Initialize from URL parameter or provided prop
    return getUrlParam(URL_PARAM_NAME) || url || "";
  });
  
  const containerApplication = getContainerActions();
  const { corsStatus, sasToken } = useContainer();
  const showCorsHelp = useCorsHelp();

  // Validate URL from parameter on initial load
  useEffect(() => {
    const urlFromParams = getUrlParam(URL_PARAM_NAME);
    if (urlFromParams) {
      console.log("Auto-loading and validating URL from params:", urlFromParams);
      containerApplication.setContainerUrl(urlFromParams);
    }
  }, [getUrlParam, containerApplication]); // Run when dependencies change

  // Save container URL and update browser URL
  const handleSave = () => {
    console.log("Manual save and validate URL:", urlValue);
    containerApplication.setContainerUrl(urlValue);
  };

  // Show CORS help when error occurs
  useEffect(() => {
    if (corsStatus?.error) {
      console.log("CORS error detected, showing help modal.");
      showCorsHelp();
    }
  }, [corsStatus]);

  return (
    <div className="border-b pb-4">
      <Text variant="label" align="left">
        Container
      </Text>
      
      <UrlInput
        value={urlValue}
        onChange={setUrlValue}
        onSave={handleSave}
      />
      
      <ContainerStatus container={container} />
      <CorsError corsStatus={corsStatus} />
    </div>
  );
};
